import { useEffect, useCallback } from "react";
import {
  INACTIVE_SESSION_TIME_CHECK_SLEEP_MODE,
  SET_POPUP_IDEAL_TIMEOUT_DURATION,
} from "../assets/constants/constants";
import { clearLocalStorage } from "../lib/utils";
import { useNavigate } from "react-router-dom";

interface UseIdleSessionTimerProps {
  timeout: number;
  onIdle: () => void;
}

const useIdleSessionTimer = ({ timeout, onIdle }: UseIdleSessionTimerProps) => {
  const navigate = useNavigate();
  // Function to update the last active timestamp in sessionStorage
  const updateLastActiveTimestamp = useCallback(() => {
    sessionStorage.setItem("lastActiveTimestamp", Date.now().toString());
  }, []);

  // Function to reset timers and update sessionStorage
  const resetTimers = useCallback(() => {
    updateLastActiveTimestamp();
    // If popup is visible, also clear the popup expiry timestamp
    if (sessionStorage.getItem("popupExpiryTimestamp")) {
      sessionStorage.removeItem("popupExpiryTimestamp");
      // Dispatch a storage event to notify other tabs
      window.dispatchEvent(new Event("storage"));
    }
  }, [updateLastActiveTimestamp]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // Initialize lastActiveTimestamp in sessionStorage if not exists
    if (!sessionStorage.getItem("lastActiveTimestamp")) {
      updateLastActiveTimestamp();
    }

    // Events to track user activity
    const events: string[] = [
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "popstate",
    ];

    // Function to reset the timer when any event is triggered
    const handleUserActivity = () => {
      updateLastActiveTimestamp();
      clearTimeout(timer);
      timer = setTimeout(onIdle, timeout);
    };

    // Handle visibility change (sleep mode, tab change, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearTimeout(timer); // Clear the timer when tab is hidden
      } else if (document.visibilityState === "visible") {
        // When tab becomes visible again, check if we should show the popup
        const lastActive = Number(
          sessionStorage.getItem("lastActiveTimestamp")
        );
        const currentTime = Date.now();
        const totalIdleTime = currentTime - lastActive;
        const totalAllowedTime = timeout + SET_POPUP_IDEAL_TIMEOUT_DURATION;

        // If total idle time exceeds combined timeout, force logout
        if (totalIdleTime >= totalAllowedTime) {
          // localStorage.clear();
          // sessionStorage.clear();
          clearLocalStorage();
          // window.location.href = "/";
          navigate("/");
          return;
        }
        // If total idle time exceeds initial timeout but less than combined, show popup
        else if (totalIdleTime >= timeout) {
          // Set popup expiry timestamp with remaining time from total allowed time
          const remainingTime = totalAllowedTime - totalIdleTime;
          const expiryTime = currentTime + remainingTime;
          sessionStorage.setItem("popupExpiryTimestamp", expiryTime.toString());
          onIdle();
        } else {
          // Otherwise reset the timer for the remaining time
          clearTimeout(timer);
          timer = setTimeout(onIdle, timeout - totalIdleTime);
        }
      }
    };

    // Handle storage events for cross-tab synchronization
    const handleStorageChange = (event: StorageEvent) => {
      // If another tab has updated lastActiveTimestamp or cleared popupExpiryTimestamp
      if (
        event.key === "lastActiveTimestamp" ||
        event.key === "popupExpiryTimestamp"
      ) {
        // Reset our local timer based on the new timestamp
        clearTimeout(timer);

        const lastActive = Number(
          sessionStorage.getItem("lastActiveTimestamp")
        );
        const currentTime = Date.now();
        const elapsedTime = currentTime - lastActive;

        // If we're still within the timeout period, set a new timer
        if (elapsedTime < timeout) {
          timer = setTimeout(onIdle, timeout - elapsedTime);
        }
      }
    };

    // Check for sleep mode or long inactivity periods
    const checkInactivityAfterSleep = () => {
      const lastActive = Number(sessionStorage.getItem("lastActiveTimestamp"));
      const popupExpiry = sessionStorage.getItem("popupExpiryTimestamp");
      const currentTime = Date.now();
      const totalIdleTime = currentTime - lastActive;
      const totalAllowedTime = timeout + SET_POPUP_IDEAL_TIMEOUT_DURATION;

      // Force logout if total idle time exceeds combined timeout (including sleep time)
      if (totalIdleTime >= totalAllowedTime) {
        // User has been idle/asleep longer than combined timeout, force logout
        // localStorage.clear();
        // sessionStorage.clear();
        clearLocalStorage();

        // Force redirect to login page
        // window.location.href = "/";
        navigate("/");
        return;
      }

      // If popup was shown and its expiry time has passed, log out
      if (popupExpiry && currentTime > Number(popupExpiry)) {
        // Clear session storage and trigger logout
        sessionStorage.removeItem("lastActiveTimestamp");
        sessionStorage.removeItem("popupExpiryTimestamp");
        onIdle(); // This will trigger the popup, which will auto-logout if no action is taken
        return;
      }

      // If user has been inactive longer than initial timeout but less than combined, show popup
      if (totalIdleTime >= timeout && !popupExpiry) {
        onIdle();
      }
    };

    // Set up periodic check for sleep mode
    const inactivityCheckInterval = setInterval(
      checkInactivityAfterSleep,
      INACTIVE_SESSION_TIME_CHECK_SLEEP_MODE
    );

    // Listen for iframe activity
    const captureEvent = (event: any) => {
      if (event?.data?.type === "field:data-change") {
        handleUserActivity();
      }
    };

    // Add event listeners
    events.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("message", captureEvent);
    window.addEventListener("storage", handleStorageChange);

    // Initialize the timer
    timer = setTimeout(onIdle, timeout);

    // Cleanup
    return () => {
      clearTimeout(timer);
      clearInterval(inactivityCheckInterval);
      events.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
      window.removeEventListener("message", captureEvent);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [timeout, onIdle, updateLastActiveTimestamp]);

  // Function to directly logout user without showing popup
  const logoutUser = useCallback(() => {
    clearLocalStorage();
    // localStorage.clear();
    // sessionStorage.clear();
    // Other logout logic will be handled by the component using this hook
  }, []);

  // Return functions that can be called from outside the hook
  return {
    resetTimers,
    logoutUser,
  };
};

export default useIdleSessionTimer;